// src/components/OnboardingModal.jsx
//
// Non-dismissable onboarding modal for business users.
// Collects name, timezone, and weekly working hours.
// Writes to profiles (UPDATE) and schedules (INSERT) on submit.

import { useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { COMMON_TIMEZONES, tzLabel } from '../constants/timezones';
import Modal from './Modal';
import toast from 'react-hot-toast';
import styles from './OnboardingModal.module.css';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

const DEFAULT_HOURS = DAYS.map(day => ({
  day,
  enabled: day !== 'saturday' && day !== 'sunday',
  start: '09:00',
  end: '17:00',
}));

// Build timezone options list, ensuring browser-detected tz is always included
function buildTimezoneOptions(detectedTz) {
  const list = [...COMMON_TIMEZONES];
  if (detectedTz && !list.includes(detectedTz)) {
    list.push(detectedTz);
  }
  return list;
}

// Generate HH:MM options in 30-minute intervals
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
});

export default function OnboardingModal({ onComplete }) {
  const { user, profile, updateProfile } = useAuth();

  // Detect browser timezone once
  const detectedTz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
    catch { return ''; }
  }, []);

  const timezoneOptions = useMemo(() => buildTimezoneOptions(detectedTz), [detectedTz]);

  const [name, setName] = useState(profile?.full_name || '');
  const [timezone, setTimezone] = useState(detectedTz || '');
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const updateDay = (day, updates) => {
    setHours(prev => prev.map(h => h.day === day ? { ...h, ...updates } : h));
    // Clear validation error for this day when user changes it
    if (validationErrors[day]) {
      setValidationErrors(prev => { const next = { ...prev }; delete next[day]; return next; });
    }
  };

  const validate = () => {
    const errors = {};
    if (!name.trim()) {
      errors._name = 'Name is required';
    }
    if (!timezone) {
      errors._timezone = 'Please select a timezone';
    }
    const enabledDays = hours.filter(h => h.enabled);
    if (enabledDays.length === 0) {
      errors._schedule = 'Enable at least one working day';
    }
    for (const h of enabledDays) {
      if (h.start >= h.end) {
        errors[h.day] = 'End time must be after start time';
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setError('');

    try {
      // 1. Update profile
      const { error: profileError } = await updateProfile({
        full_name: name.trim(),
        timezone,
      });
      if (profileError) throw profileError;

      // 2. Insert default schedule
      const weeklyHours = hours
        .filter(h => h.enabled)
        .map(({ day, start, end }) => ({ day, start, end }));

      const { error: scheduleError } = await supabase
        .from('schedules')
        .insert({
          user_id: user.id,
          name: 'Default',
          timezone,
          weekly_hours: weeklyHours,
          is_default: true,
        });

      if (scheduleError) throw scheduleError;

      toast.success("You're all set!");
      onComplete();
    } catch (err) {
      console.error('Onboarding submit failed:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={() => {}} // no-op — non-dismissable
      title="Set up your availability"
      size="md"
      scrollable={false}
      disableBackdropClose={true}
      escapeDeactivates={false}
    >
      <header className={styles.header}>
        <h2 className={styles.title}>Set up your availability</h2>
        <p className={styles.subtitle}>
          We need a few details before your booking links can accept appointments.
        </p>
      </header>

      <main className={styles.body}>
        {/* Name */}
        <div className={styles.field}>
          <label className={styles.label} htmlFor="onboard-name">Your name</label>
          <input
            id="onboard-name"
            className="input"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Full name"
          />
          {validationErrors._name && <span className={styles.fieldError}>{validationErrors._name}</span>}
        </div>

        {/* Timezone */}
        <div className={styles.field}>
          <label className={styles.label} htmlFor="onboard-tz">Timezone</label>
          <select
            id="onboard-tz"
            className="input"
            value={timezone}
            onChange={e => setTimezone(e.target.value)}
          >
            <option value="">Select timezone...</option>
            {timezoneOptions.map(tz => (
              <option key={tz} value={tz}>{tzLabel(tz)}</option>
            ))}
          </select>
          {validationErrors._timezone && <span className={styles.fieldError}>{validationErrors._timezone}</span>}
        </div>

        {/* Working hours */}
        <div className={styles.field}>
          <div className={styles.scheduleHeader}>
            <span className={styles.label}>Working hours</span>
          </div>
          {validationErrors._schedule && <span className={styles.fieldError}>{validationErrors._schedule}</span>}
          {hours.map(({ day, enabled, start, end }) => (
            <div key={day} className={styles.dayRow}>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  className={styles.toggleInput}
                  checked={enabled}
                  onChange={e => updateDay(day, { enabled: e.target.checked })}
                />
                <span className={styles.toggleTrack} />
              </label>
              <span className={enabled ? styles.dayLabel : styles.dayLabelOff}>
                {DAY_LABELS[day]}
              </span>
              {enabled ? (
                <>
                  <select
                    className={`input input-sm ${styles.timeInput}`}
                    value={start}
                    onChange={e => updateDay(day, { start: e.target.value })}
                  >
                    {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <span className={styles.timeSep}>&ndash;</span>
                  <select
                    className={`input input-sm ${styles.timeInput}`}
                    value={end}
                    onChange={e => updateDay(day, { end: e.target.value })}
                  >
                    {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </>
              ) : (
                <>
                  <span />
                  <span />
                  <span />
                </>
              )}
              {validationErrors[day] && (
                <span className={styles.fieldError} style={{ gridColumn: '2 / -1' }}>
                  {validationErrors[day]}
                </span>
              )}
            </div>
          ))}
        </div>
      </main>

      <footer className={styles.footer}>
        {error && <span className={styles.error}>{error}</span>}
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Save & continue'}
        </button>
      </footer>
    </Modal>
  );
}
